/**
 * API Configuration Management
 * Canonical environment-aware configuration for API endpoints
 */

export interface ApiEndpoints {
	auth: string;
	gateway: string;
	tenant: string;
	packages: string;
	analytics: string;
	storage: string;
	webhooks: string;
}

export interface ApiConfig {
	baseURL: string;
	endpoints: ApiEndpoints;
	timeout: number;
	retries: number;
	environment: "development" | "staging" | "production";
	features: {
		enableDebugLogs: boolean;
		enableRetries: boolean;
		enableCaching: boolean;
	};
}

/**
 * Environment-specific API configurations
 */
const API_CONFIGURATIONS: Record<string, ApiConfig> = {
	development: {
		baseURL:
			import.meta.env.VITE_API_BASE_URL ||
			"https://auth-service.cto-41f.workers.dev",
		endpoints: {
			auth: "/api/auth",
			gateway: "/api/v1",
			tenant: "/api/t",
			packages: "/api/v1/packages",
			analytics: "/api/v1/analytics",
			storage: "/api/v1/blobs",
			webhooks: "/api/v1/webhooks",
		},
		timeout: 30000,
		retries: 3,
		environment: "development",
		features: {
			enableDebugLogs: true,
			enableRetries: true,
			enableCaching: false,
		},
	},

	staging: {
		baseURL:
			import.meta.env.VITE_API_BASE_URL ||
			"https://staging-gateway.artify.monster",
		endpoints: {
			auth: "/api/auth",
			gateway: "/api/v1",
			tenant: "/api/t",
			packages: "/api/v1/packages",
			analytics: "/api/v1/analytics",
			storage: "/api/v1/blobs",
			webhooks: "/api/v1/webhooks",
		},
		timeout: 20000,
		retries: 2,
		environment: "staging",
		features: {
			enableDebugLogs: true,
			enableRetries: true,
			enableCaching: true,
		},
	},

	production: {
		baseURL:
			import.meta.env.VITE_API_BASE_URL || "https://tenant.artify.monster",
		endpoints: {
			auth: "/api/auth",
			gateway: "/api/v1",
			tenant: "/api/t",
			packages: "/api/v1/packages",
			analytics: "/api/v1/analytics",
			storage: "/api/v1/blobs",
			webhooks: "/api/v1/webhooks",
		},
		timeout: 15000,
		retries: 2,
		environment: "production",
		features: {
			enableDebugLogs: false,
			enableRetries: true,
			enableCaching: true,
		},
	},
};

/**
 * Get current environment from Vite environment variables
 */
function getCurrentEnvironment(): keyof typeof API_CONFIGURATIONS {
	const env =
		import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";

	if (env in API_CONFIGURATIONS) {
		return env as keyof typeof API_CONFIGURATIONS;
	}

	// Default to development for unknown environments
	return "development";
}

/**
 * Get API configuration for current environment
 */
export function getApiConfig(): ApiConfig {
	const environment = getCurrentEnvironment();
	return API_CONFIGURATIONS[environment];
}

/**
 * Build full URL for an endpoint
 */
export function buildApiUrl(
	endpoint: keyof ApiEndpoints,
	path: string = "",
): string {
	const config = getApiConfig();
	const endpointPath = config.endpoints[endpoint];
	const cleanPath = path.startsWith("/") ? path : `/${path}`;
	return `${config.baseURL}${endpointPath}${cleanPath}`;
}

/**
 * Build tenant-scoped URL
 */
export function buildTenantUrl(tenantSlug: string, path: string = ""): string {
	const config = getApiConfig();
	const cleanPath = path.startsWith("/") ? path : `/${path}`;
	return `${config.baseURL}${config.endpoints.tenant}/${tenantSlug}${cleanPath}`;
}

/**
 * Check if debug logging is enabled
 */
export function isDebugEnabled(): boolean {
	const config = getApiConfig();
	return config.features.enableDebugLogs;
}
